import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetJobCardService } from "subModule/src/services/jobcard/useJobCards";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, SelectChangeEvent } from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as JobCard } from "assets/Modal/jobcard.svg";
import Loader from "layout/Loader";
import JobCardSearchHeader from "components/JobCard/JobCardSearchHeader";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import { CommonProfileService } from "subModule/src/core/services";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import TableWithPagination from "common/Table/Table";
interface SearchFormData {
  [key: string]: string | any;
}
interface JobCardProps {
  vehicleId?: number;
}
export default function JobCardListing(props: JobCardProps) {
  const navigate = useNavigate();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(props?.vehicleId ? 5 : 20);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["jobCardNo", "status"]);
  const [search, setSearch] = useState<any>({ status: 201 });
  const [downloading, setDownloading] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    jobCardNo: "",
    stationId: 0,
    stationName: "",
    vehiclePlate: "",
    vehicleVin: "",
    status: 201,
    start: null,
    end: null,
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetJobCardService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    type: 2,
    ...(props?.vehicleId && { vehicleId: props.vehicleId }),
  });
  const { data: stationData, refetch: stationRefetch } =
    useGetStationDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      name: dropdownSearchTerm,
    });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const JobCardStatusList = [
    { label: t("all"), value: "" },
    { label: t("open"), value: 201 },
    { label: t("closed"), value: 203 },
    { label: t("cancelled"), value: 202 },
  ];
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    stationRefetch();
  }, []);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.status]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.start, searchFormData.end]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stations")
        stationRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const showRowView = (row: any) => navigate("/job-card/" + row.id);
  const clearSearch = () => {
    setFilter(["jobCardNo", "status"]);
    setSearchFormData({
      jobCardNo: "",
      stationId: 0,
      vehiclePlate: "",
      vehicleVin: "",
      status: 201,
      start: null,
      end: null,
    });
    setSearch({ status: 201 });
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    const selectedFilters =
      typeof value === "string" ? value.split(",") : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      );
      if (selectedFilters.includes("stationId") && prevFormData.stationName)
        updatedFormData.stationName = prevFormData.stationName;
      if (selectedFilters.includes("dateInterval")) {
        updatedFormData.start = prevFormData.start;
        updatedFormData.end = prevFormData.end;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "stationId"
        ? value
          ? parseInt(value.value) || 0
          : 0
        : field === "status"
          ? value
            ? value.value
            : ""
          : value;
    if (field === "status" && searchFormData.status === val) return;
    if (field === "dateInterval") {
      setSearchFormData((prevData) => ({
        ...prevData,
        start: value[0],
        end: value[1],
      }));
      if ((value[0] && value[1]) || (value[0] === null && value[1] === null)) {
        setLoading(true);
      }
    } else {
      setSearchFormData((prevData) => ({
        ...prevData,
        [field]: val,
        ...(field === "stationId" && value?.label
          ? { stationName: value.label }
          : {}),
      }));
    }
    if (field === "status") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    const { start, end, ...rest } = searchFormData;
    if (start && end) {
      filteredSearch.start = start;
      filteredSearch.end = end;
    } else {
      // ❗ If one or both missing → remove both
      setSearchFormData((prev) => ({
        ...prev,
        start: null,
        end: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  // const adjustActions = (actions: any[]) => {
  //   return checkPrivileges("ROLE_JOBCARD_UPDATE")
  //     ? checkPrivileges("ROLE_JOBCARD_ARCHIVE")
  //       ? actions
  //       : actions.filter((action) => action.label !== "delete")
  //     : checkPrivileges("ROLE_JOBCARD_ARCHIVE")
  //       ? actions.filter((action) => action.label !== "edit")
  //       : [];
  // };
  const DownloadJobCards = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search, ...(props?.vehicleId && { vehicleId: props.vehicleId }) },
      "jobcards/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `JobCards.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  return (
    <Box>
      {props?.vehicleId ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={false}
          onClick={() => navigate("/job-card/:id")}
          heading={t("jobCards")}
          icon={<JobCard height={28} width={28} />}
          btnText={"create"}
          btnType={"create"}
          isBtnEnabled={false}
          showCreateBtn={false}
        />
      )}
      {isFetching || loading ? (
        <Loader />
      ) : (
        <TableWithPagination
          columns={[
            // { id: 'image', label: '', numeric: false },
            { id: "vehicleDetail", label: "vehicle", numeric: false },
            { id: "plate", label: "plateNumber", numeric: false },
            { id: "entityNo", label: "jobCardNo", numeric: false },
            { id: "customerName", label: "customerName", numeric: false },
            { id: "active", label: "status", numeric: false },
            { id: "vehicleVin", label: "vehicleNumber", numeric: false },
            { id: "stationName", label: "station", numeric: false },
            { id: "createdAt", label: "createdAt", numeric: false },
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          tableData={tableData?.pages[0]?.data || {}}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          showRowView={showRowView}
          rowClick={checkPrivileges("ROLE_JOBCARD_UPDATE")}
          type="jobCard"
          {...{ pageNo, pageSize }}
          loadingSearch={false}
          headerComponent={
            <>
              <JobCardSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                stationData={stationData?.pages[0]?.data?.content || []}
                stationResponse={stationData || {}}
                handleDropdownActions={handleDropdownActions}
                {...{
                  filter,
                  search,
                  JobCardStatusList,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
              />
              {checkPrivileges("ROLE_JOBCARD_EXCEL_EXPORT") ? (
                <Box className="table-header-actions">
                  <Button
                    id="import-button"
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setDownloading(true);
                      DownloadJobCards();
                    }}
                    className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
                    sx={{
                      outline: "2px solid",
                      color: themeColors?.primary,
                      borderColor: themeColors?.primary,
                      height: 34,
                      minHeight: 34,
                    }}
                    disabled={downloading}
                  >
                    {downloading ? (
                      <CircularProgress
                        sx={{ color: themeColors?.primary, marginRight: "8px" }}
                        size={16}
                      />
                    ) : null}
                    {t("export")}
                  </Button>
                </Box>
              ) : null}
            </>
          }
          showHeader={true}
          showSearch={true}
          showFilter={true}
        />
      )}
    </Box>
  );
}
